import { cva, type VariantProps } from "class-variance-authority";

/*
 * Варианты атомов — только на семантических токенах (surface-*, ink-*, line,
 * accent, risk-*). Это единственный источник вида кнопок, полей, бейджей и
 * панелей: страницы не собирают их из сырых классов палитры.
 */

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-medium rounded border border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        // Основное действие экрана — одно; brand-оранжевый с тёмным текстом.
        primary: "bg-accent text-on-accent hover:bg-accent-hover",
        secondary: "bg-surface-hover text-ink-1 border-line hover:bg-surface-pressed",
        danger: "bg-risk-d text-white hover:bg-risk-d/90",
        ghost: "text-ink-2 hover:text-ink-1 hover:bg-surface-hover",
        outline: "bg-transparent text-ink-1 border-line-strong hover:bg-surface-hover",
      },
      size: {
        sm: "h-8 px-3 text-fs-sm",
        md: "h-10 px-4 text-fs-sm",
        lg: "h-11 px-5 text-fs-sm",
      },
      fullWidth: {
        true: "w-full",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;

// Панель: плоскость elevated и волосяная линия. Тени у панелей нет —
// глубина в этой системе строится линиями, параметр shadow оставлен для
// совместимости и ничего не добавляет.
export const cardVariants = cva("bg-surface-elevated border border-line", {
  variants: {
    padding: {
      none: "",
      sm: "p-4",
      md: "p-5",
      lg: "p-6",
    },
    rounded: {
      md: "rounded-card",
      lg: "rounded-card",
      xl: "rounded-card",
    },
    interactive: {
      true: "transition-colors cursor-pointer hover:border-line-strong hover:bg-surface-hover/40",
    },
    shadow: {
      none: "",
      sm: "",
      md: "",
      lg: "",
    },
  },
  defaultVariants: {
    padding: "md",
    rounded: "lg",
    shadow: "none",
  },
});

export type CardVariants = VariantProps<typeof cardVariants>;

export const inputVariants = cva(
  "w-full rounded border border-line-strong bg-surface-elevated text-ink-1 placeholder:text-ink-4 transition-colors focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent-ring disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "",
        error: "border-risk-d focus:border-risk-d focus:ring-risk-d/30",
        success: "border-risk-a focus:border-risk-a focus:ring-risk-a/30",
      },
      size: {
        sm: "h-8 px-2.5 text-fs-sm",
        md: "h-10 px-3 text-fs-lg",
        lg: "h-11 px-4 text-fs-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
);

export type InputVariants = VariantProps<typeof inputVariants>;

// Бейдж — прямоугольная метка 4 px. Цвет только у семантики; primary
// (brand) у бейджа означает «выбрано/активно», а не украшение.
export const badgeVariants = cva("inline-flex items-center gap-1 rounded font-medium whitespace-nowrap", {
  variants: {
    variant: {
      default: "bg-surface-hover text-ink-2",
      primary: "bg-accent/12 text-accent",
      success: "bg-risk-a/12 text-risk-a",
      warning: "bg-risk-b/12 text-risk-b",
      danger: "bg-risk-d/12 text-risk-d",
      info: "bg-risk-fyi/12 text-risk-fyi",
    },
    size: {
      sm: "h-5 px-1.5 text-fs-2xs",
      md: "h-6 px-2 text-fs-xs",
      lg: "h-7 px-2.5 text-fs-sm",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "md",
  },
});

export type BadgeVariants = VariantProps<typeof badgeVariants>;
